export interface ImagePalette {
  primary: string;
  accent: string;
}

interface ColorBucket {
  count: number;
  r: number;
  g: number;
  b: number;
}

function toHex(bucket: ColorBucket): string {
  const r = Math.round(bucket.r / bucket.count);
  const g = Math.round(bucket.g / bucket.count);
  const b = Math.round(bucket.b / bucket.count);
  return `#${[r, g, b].map(v => v.toString(16).padStart(2, "0")).join("")}`;
}

function bucketAvg(bucket: ColorBucket): { r: number; g: number; b: number } {
  return { r: bucket.r / bucket.count, g: bucket.g / bucket.count, b: bucket.b / bucket.count };
}

// Blends a color toward white so it stays legible as an accent/border on a near-black background.
function ensureLegible(bucket: ColorBucket): string {
  const { r, g, b } = bucketAvg(bucket);
  const lightness = (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
  if (lightness >= 90) return toHex(bucket);
  const blend = 0.4;
  const lr = Math.round(r + (255 - r) * blend);
  const lg = Math.round(g + (255 - g) * blend);
  const lb = Math.round(b + (255 - b) * blend);
  return `#${[lr, lg, lb].map(v => v.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Samples an image's pixels client-side and returns two dominant, legible colors
 * for use as a theme (primary/accent). Resolves null on any failure (load error,
 * CORS-tainted canvas, timeout) so callers can fall back to a default theme.
 */
export function extractImagePalette(imageUrl: string): Promise<ImagePalette | null> {
  return new Promise(resolve => {
    let settled = false;
    const finish = (result: ImagePalette | null) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };

    const timeout = setTimeout(() => finish(null), 8000);

    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onerror = () => {
      clearTimeout(timeout);
      finish(null);
    };

    img.onload = () => {
      clearTimeout(timeout);
      try {
        const size = 48;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          finish(null);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        const buckets = new Map<string, ColorBucket>();
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (a < 128) continue;

          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const lightness = (max + min) / 2;
          const saturation = max === min ? 0 : (max - min) / (255 - Math.abs(2 * lightness - 255));
          // Skip near-black, near-white, and low-saturation (gray) pixels so the
          // palette reflects actual poster colors rather than margins/shadows.
          if (lightness < 25 || lightness > 235 || saturation < 0.15) continue;

          const key = `${Math.round(r / 24)}-${Math.round(g / 24)}-${Math.round(b / 24)}`;
          const bucket = buckets.get(key);
          if (bucket) {
            bucket.count++;
            bucket.r += r;
            bucket.g += g;
            bucket.b += b;
          } else {
            buckets.set(key, { count: 1, r, g, b });
          }
        }

        const sorted = [...buckets.values()].sort((a, b) => b.count - a.count);
        if (sorted.length === 0) {
          finish(null);
          return;
        }

        const primary = ensureLegible(sorted[0]);
        const accent = sorted.length > 1 ? ensureLegible(sorted[1]) : primary;

        finish({ primary, accent });
      } catch {
        finish(null);
      }
    };

    img.src = imageUrl;
  });
}
